import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Badge, Button, Input } from "@vimos/ui";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../lib/auth";
import { notifyCmsChanged, type CmsTable } from "../../lib/cms-webhook";
import { getContentConfig, type ContentConfig } from "./contentConfig";

type Row = Record<string, unknown> & {
  id: string;
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
  sort_order?: number;
};

type StatusFilter = "all" | "published" | "draft";
type SortKey = "recent" | "oldest" | "title" | "order";

const pageSize = 8;

async function fetchRows(config: ContentConfig): Promise<Row[]> {
  const orderField = config.orderField ?? "created_at";
  const { data, error } = await supabase
    .from(config.table)
    .select("*")
    .order(orderField, { ascending: true });
  if (error) throw error;
  return (data ?? []) as Row[];
}

function rowText(row: Row, config: ContentConfig): string {
  return [
    row[config.titleField],
    config.subtitleField ? row[config.subtitleField] : "",
    row.slug,
    row.category,
    row.summary,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function formatDate(value: unknown): string {
  if (!value || typeof value !== "string") return "Sin fecha";
  return new Intl.DateTimeFormat("es-PE", { dateStyle: "medium" }).format(new Date(value));
}

export function ContentListPage({ section }: { section: string }) {
  const config = getContentConfig(section);
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("recent");
  const [page, setPage] = useState(1);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    data: rows,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["content", config.table],
    queryFn: () => fetchRows(config),
  });

  const togglePublished = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) => {
      const payload: Record<string, unknown> = { is_published: !isPublished };
      if (!isPublished && (config.table === "pages" || config.table === "case_studies")) {
        payload.published_at = new Date().toISOString();
      }
      const { error: updateError } = await supabase
        .from(config.table)
        .update(payload as never)
        .eq("id", id);
      if (updateError) throw updateError;
      return id;
    },
    onSuccess: async (id: string) => {
      queryClient.invalidateQueries({ queryKey: ["content", config.table] });

      try {
        await notifyCmsChanged(config.table as CmsTable, "update", id, session?.access_token);
      } catch (webhookError) {
        console.error("Error al notificar cambio CMS", webhookError);
      }
    },
  });

  const deleteRow = useMutation({
    mutationFn: async (id: string) => {
      setDeleteError(null);
      if (config.table === "blog_categories") {
        const { count, error: countError } = await supabase
          .from("blog_posts")
          .select("id", { count: "exact", head: true })
          .eq("category_id", id);
        if (countError) throw countError;
        if ((count ?? 0) > 0) {
          throw new Error(
            "Esta categoria esta en uso por publicaciones. Cambia esas publicaciones antes de eliminarla.",
          );
        }
      }
      const { error: deleteError } = await supabase.from(config.table).delete().eq("id", id);
      if (deleteError) throw deleteError;
      return id;
    },
    onSuccess: async (id: string) => {
      queryClient.invalidateQueries({ queryKey: ["content", config.table] });

      try {
        await notifyCmsChanged(config.table as CmsTable, "delete", id, session?.access_token);
      } catch (webhookError) {
        console.error("Error al notificar cambio CMS", webhookError);
      }
    },
    onError: (deleteIssue) => {
      setDeleteError(
        deleteIssue instanceof Error ? deleteIssue.message : "No pudimos eliminar este registro.",
      );
    },
  });

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const result = (rows ?? [])
      .filter((row) => !normalizedQuery || rowText(row, config).includes(normalizedQuery))
      .filter((row) => {
        if (!config.hasPublished || statusFilter === "all") return true;
        return statusFilter === "published" ? Boolean(row.is_published) : !row.is_published;
      })
      .sort((a, b) => {
        if (sortKey === "title") {
          return String(a[config.titleField] ?? "").localeCompare(String(b[config.titleField] ?? ""));
        }
        if (sortKey === "order") return Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
        const left = new Date(String(a.updated_at ?? a.created_at ?? 0)).getTime();
        const right = new Date(String(b.updated_at ?? b.created_at ?? 0)).getTime();
        return sortKey === "oldest" ? left - right : right - left;
      });
    return result;
  }, [config, query, rows, sortKey, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const publishedCount = rows?.filter((row) => row.is_published).length ?? 0;
  const draftCount = config.hasPublished ? (rows?.length ?? 0) - publishedCount : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-signal-hover font-mono text-xs uppercase">Contenido</p>
          <h1 className="font-display text-ink mt-2 text-3xl font-bold">{config.title}</h1>
          <p className="text-mist mt-1 max-w-2xl text-sm">{config.description}</p>
        </div>
        <Link to={`/${section}/nuevo`}>
          <Button>Crear registro</Button>
        </Link>
      </div>

      <section className="border-sand/40 bg-paper shadow-card grid gap-3 rounded-md border p-4 lg:grid-cols-[1fr_auto_auto]">
        <Input
          label="Buscar"
          placeholder="Nombre, slug, categoria o resumen"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(1);
          }}
        />
        {config.hasPublished ? (
          <div className="flex flex-col gap-1.5">
            <label className="font-body text-ink text-sm font-medium">Estado</label>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as StatusFilter);
                setPage(1);
              }}
              className="border-sand/70 bg-paper text-ink h-11 rounded-md border px-3.5 text-sm"
            >
              <option value="all">Todos</option>
              <option value="published">Publicados</option>
              <option value="draft">Borradores</option>
            </select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <label className="font-body text-ink text-sm font-medium">Ordenar</label>
          <select
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value as SortKey)}
            className="border-sand/70 bg-paper text-ink h-11 rounded-md border px-3.5 text-sm"
          >
            <option value="recent">Actualizados recientes</option>
            <option value="oldest">Mas antiguos</option>
            <option value="title">Titulo</option>
            <option value="order">Orden editorial</option>
          </select>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <article className="border-sand/40 bg-paper shadow-card rounded-md border p-4">
          <p className="text-mist text-xs uppercase">Total</p>
          <p className="font-display text-ink mt-2 text-2xl font-bold">{rows?.length ?? 0}</p>
        </article>
        <article className="border-sand/40 bg-paper shadow-card rounded-md border p-4">
          <p className="text-mist text-xs uppercase">Publicados</p>
          <p className="font-display text-ink mt-2 text-2xl font-bold">
            {config.hasPublished ? publishedCount : "-"}
          </p>
        </article>
        <article className="border-sand/40 bg-paper shadow-card rounded-md border p-4">
          <p className="text-mist text-xs uppercase">Borradores</p>
          <p className="font-display text-ink mt-2 text-2xl font-bold">
            {config.hasPublished ? draftCount : "-"}
          </p>
        </article>
      </section>

      {isLoading && <p className="text-mist">Cargando...</p>}
      {error && <p className="text-red-600">No pudimos cargar esta seccion.</p>}
      {deleteError ? (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {deleteError}
        </p>
      ) : null}

      <div className="border-sand/40 bg-paper shadow-card overflow-hidden rounded-md border">
        <div className="border-sand/40 text-mist hidden grid-cols-[1fr_140px_150px_280px] border-b px-4 py-3 text-xs uppercase lg:grid">
          <span>Registro</span>
          <span>Estado</span>
          <span>Fecha</span>
          <span className="text-right">Acciones</span>
        </div>

        {paginatedRows.length === 0 ? (
          <div className="text-mist p-8 text-sm">No hay registros que coincidan con los filtros.</div>
        ) : null}

        <ul className="divide-sand/40 divide-y">
          {paginatedRows.map((row) => (
            <li
              key={row.id}
              className="grid gap-3 px-4 py-4 lg:grid-cols-[1fr_140px_150px_280px] lg:items-center"
            >
              <div>
                <Link to={`/${section}/${row.id}`} className="text-ink hover:text-signal font-medium">
                  {String(row[config.titleField] ?? "Sin titulo")}
                </Link>
                {config.subtitleField && row[config.subtitleField] ? (
                  <p className="text-mist mt-1 line-clamp-2 text-sm">{String(row[config.subtitleField])}</p>
                ) : null}
              </div>
              <div>
                {config.hasPublished ? (
                  <Badge tone={row.is_published ? "market" : "neutral"}>
                    {row.is_published ? "Publicado" : "Borrador"}
                  </Badge>
                ) : (
                  <Badge>Registro</Badge>
                )}
              </div>
              <p className="text-mist text-sm">{formatDate(row.updated_at ?? row.created_at)}</p>
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {config.hasPublished ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() =>
                      togglePublished.mutate({ id: row.id, isPublished: Boolean(row.is_published) })
                    }
                  >
                    {row.is_published ? "Despublicar" : "Publicar"}
                  </Button>
                ) : null}
                <Link to={`/${section}/${row.id}`}>
                  <Button size="sm" variant="outline" type="button">
                    Editar
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="danger"
                  type="button"
                  onClick={() => {
                    if (window.confirm("Esta accion eliminara el registro. Deseas continuar?"))
                      deleteRow.mutate(row.id);
                  }}
                >
                  Eliminar
                </Button>
              </div>
            </li>
          ))}
        </ul>

        <div className="border-sand/40 text-mist flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p>
            Mostrando {paginatedRows.length} de {filteredRows.length} registros.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((item) => item - 1)}
            >
              Anterior
            </Button>
            <span className="border-sand/50 text-ink inline-flex h-9 items-center rounded-md border px-3">
              {currentPage} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((item) => item + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
